import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, RefreshCw, ClipboardList, Clock, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';

interface JotformStatus {
  connected: boolean;
  formId: string | null;
  formTitle: string | null;
  submissionCount: number;
  lastSubmission: string | null;
  error: string | null;
}

const JotformConnect = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<JotformStatus>({
    connected: false,
    formId: null,
    formTitle: null,
    submissionCount: 0,
    lastSubmission: null,
    error: null,
  });

  const checkConnection = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('test-jotform');
      
      if (error) throw error;
      
      setStatus({
        connected: data.success,
        formId: data.formId || null,
        formTitle: data.formTitle || null,
        submissionCount: data.submissionCount || 0,
        lastSubmission: data.lastSubmission || null,
        error: data.success ? null : data.message,
      });
    } catch (error) {
      console.error('Error checking Jotform connection:', error);
      setStatus({
        connected: false,
        formId: null,
        formTitle: null,
        submissionCount: 0,
        lastSubmission: null,
        error: error instanceof Error ? error.message : 'Connection failed',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, []);

  const handleRefresh = () => {
    checkConnection();
    toast({
      title: 'Refreshing...',
      description: 'Checking Jotform connection status',
    });
  };

  return (
    <Card className="overflow-hidden flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-orange-100 dark:bg-orange-900/30 shrink-0">
            <ClipboardList className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">Jotform</CardTitle>
            <CardDescription className="text-xs">Appointment availability</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={loading}
            className="shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Connection Status */}
            <div className="flex items-center gap-2">
              {status.connected ? (
                <>
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Connected</span>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">Not Connected</span>
                </>
              )}
            </div>

            {/* Configuration */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Configuration</p>
              <div className="space-y-2 bg-muted/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                    status.connected ? 'bg-emerald-500' : 'bg-slate-300'
                  }`} />
                  <code className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                    JOTFORM_API_KEY
                  </code>
                </div>
                <div className="flex items-start gap-2">
                  <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                    status.formId ? 'bg-emerald-500' : 'bg-slate-300'
                  }`} />
                  <code className="text-xs font-mono text-muted-foreground break-all leading-relaxed">
                    JOTFORM_FORM_ID
                  </code>
                </div>
              </div>
            </div>

            {/* Form Details */}
            {status.connected && status.formTitle && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Form Details</p>
                <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium truncate">{status.formTitle}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ClipboardList className="h-3 w-3" />
                      {status.submissionCount} submissions
                    </span>
                    {status.lastSubmission && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(status.lastSubmission), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {status.error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300 text-xs">
                <p className="break-words [overflow-wrap:anywhere]">{status.error}</p>
              </div>
            )}

            {/* Success Message */}
            {status.connected && !status.error && (
              <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300 text-xs mt-auto">
                <p>Jotform is connected and providing availability data for bookings.</p>
              </div>
            )}

            {/* External Link */}
            {status.formId && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-2"
                onClick={() => window.open(`https://www.jotform.com/form/${status.formId}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Form in Jotform
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default JotformConnect;
